import { Button, Checkbox, Divider } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { TableCardX } from 'framework/components/cards/TableCardX';
import { QueryResultPaginationX } from 'framework/components/pagination/QueryResultPaginationX';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { useStateToggleBoolean } from 'framework/hooks/useStateToggleBoolean';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { useAccordionState } from 'framework/utils/useAccordionState';
import { IOrderFilterParams, OrderExportProp, OrderExportProps, ordersQuery_export } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { TextLinkExportButtonMenuAndDialog } from 'shared/export/TextLinkExportButtonMenuAndDialog';
import { PageReloadButtonX } from 'framework/components/layout/PageReloadButtonX';
import { PageSettingsButtonX } from 'framework/components/layout/PageSettingsButtonX';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';
import { OrdersSettingsRoute, calcOrderDetailRoute } from '../../ProcessesRoutes';
import { OrdersPageBreadcrumbs } from '../OrdersPageBreadcrumbs';
import { RegisterOrder } from '../forms/register/RegisterOrder';
import { OrdersListFilterCard } from './OrdersListFilterCard';
import { OrdersListOrderBySelect } from './OrdersListOrderBySelect';
import { OrdersQueryContext } from './OrdersQueryContext';
import { OrdersTable } from './OrdersTable';
import { createOrderExportRecord } from './createOrderExportRecord';
import { PageXHeaderActions } from 'framework/components/layout/PageXHeaderActions';

export const OrdersListPage = () => {
	const { reload, queryResult, params, setParams } = useQueryContext(OrdersQueryContext);
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext(reload);
	const [expandedId, setExpandedId] = useAccordionState<string>();
	const [expandAll, toggleExpandAll] = useStateToggleBoolean(false);
	const exportRecord = useMemo(() => createOrderExportRecord(strings), [strings]);
	const { push } = useHistory();

	const onRegister = () => {
		open(
			<RegisterOrder
				cancel={cancel}
				confirm={id => {
					push(calcOrderDetailRoute(id));
					confirm();
				}}
			/>
		);
	};

	if (queryResult === undefined) {
		return <div></div>;
	}

	return (
		<RestrictedPageX
			pageClaim='Orders'
			maxWidth={1280}
			stickyHeader={
				<div
					className='df-col fg1'
					style={{ paddingBottom: 16 }}>
					<div className='df-row-ac'>
						<OrdersPageBreadcrumbs />
						<div className='fg1'></div>
						<PageXHeaderActions>
							<PageSettingsButtonX route={OrdersSettingsRoute} />
							<PageReloadButtonX reload={reload} />
						</PageXHeaderActions>
					</div>
				</div>
			}
			stickySidebar={
				<div
					className='df-col gap-16 h100'
					style={{ padding: 16, paddingRight: 8, maxWidth: 400 }}>
					<Button
						variant='outlined'
						color='primary'
						startIcon={<AddIcon />}
						onClick={onRegister}
						style={{ borderRadius: 12 }}>
						{strings.newOrder}
					</Button>
					<OrdersListFilterCard
						params={params}
						setParams={setParams}
						metadata={queryResult.metadata}
					/>
					<div className='fg1'></div>
					<Divider />
					<TextLinkExportButtonMenuAndDialog<IOrderFilterParams, OrderExportProp>
						params={params}
						filteredCount={queryResult.filteredCount}
						unfilteredCount={queryResult.unfilteredCount}
						downloadF={ordersQuery_export}
						fileName={strings.orders}
						record={exportRecord}
						allProps={OrderExportProps}
						view='orders'
						style={{ paddingBottom: 0 }}
					/>
				</div>
			}>
			<div
				className='df-col gap-16'
				style={{
					padding: 16,
					paddingLeft: 8,
				}}>
				<SearchbarX
					placeholder={strings.searchVerb}
					value={params.searchString}
					onSearch={val => setParams(onResetToPage1({ ...params, searchString: val }))}
				/>
				<TableCardX
					header={
						<div className='df-col-ae'>
							<Button
								size='small'
								endIcon={
									<Checkbox
										checked={expandAll}
										style={{ padding: 0 }}
									/>
								}
								style={{ marginRight: -2 }}
								onClick={toggleExpandAll}>
								{strings.showAllContents}
							</Button>
							<OrdersListOrderBySelect
								sortParams={params.sortParams}
								onFilter={sortParams => setParams(onResetToPage1({ ...params, sortParams: sortParams }))}
							/>
						</div>
					}
					table={
						<OrdersTable
							queryResult={queryResult}
							sortParams={params.sortParams}
							onFilter={sp => setParams(onResetToPage1({ ...params, sortParams: sp }))}
							expandAll={expandAll}
							expandedId={expandedId}
							toggle={setExpandedId}
						/>
					}
					footer={
						<QueryResultPaginationX
							queryResult={queryResult}
							setPageParams={pageParams => setParams({ ...params, pageParams: pageParams })}
							flexEnd
						/>
					}
				/>
			</div>
		</RestrictedPageX>
	);
};
